import React from 'react';
import { Tabs, TabsList, TabsTrigger } from './ui/tabs';
import { useLocale } from '../contexts/LocaleContext';

export const TRASH_TYPE_FILTER_ALL = 'all';

export interface TrashTypeFilterOption {
  id: string;
  label: string;
  count: number;
}

interface TrashTypeFilterProps {
  value: string;
  onChange: (value: string) => void;
  options: TrashTypeFilterOption[];
}

export function getVisibleTrashTypeOptions(
  options: TrashTypeFilterOption[]
): TrashTypeFilterOption[] {
  return options.filter((option) => option.count > 0);
}

export function shouldShowTrashTypeFilter(options: TrashTypeFilterOption[]): boolean {
  return getVisibleTrashTypeOptions(options).length >= 2;
}

export function resolveTrashTypeFilter(
  selected: string,
  options: TrashTypeFilterOption[]
): string {
  const visible = getVisibleTrashTypeOptions(options);
  if (visible.length < 2) {
    return TRASH_TYPE_FILTER_ALL;
  }
  if (selected !== TRASH_TYPE_FILTER_ALL && !visible.some((option) => option.id === selected)) {
    return TRASH_TYPE_FILTER_ALL;
  }
  return selected;
}

export const TrashTypeFilter: React.FC<TrashTypeFilterProps> = ({
  value,
  onChange,
  options,
}) => {
  const { t } = useLocale();
  const visibleOptions = getVisibleTrashTypeOptions(options);
  if (visibleOptions.length < 2) {
    return null;
  }

  const totalCount = visibleOptions.reduce((sum, option) => sum + option.count, 0);
  const activeValue = resolveTrashTypeFilter(value, options);

  return (
    <Tabs value={activeValue} onValueChange={onChange} className="w-fit max-w-full">
      <TabsList className="h-8 w-fit max-w-full flex-wrap">
        <TabsTrigger value={TRASH_TYPE_FILTER_ALL} className="text-xs px-2.5 py-1 h-6">
          {t('trash_filter_all')} ({totalCount})
        </TabsTrigger>
        {visibleOptions.map((option) => (
          <TabsTrigger
            key={option.id}
            value={option.id}
            className="text-xs px-2.5 py-1 h-6"
          >
            {option.label} ({option.count})
          </TabsTrigger>
        ))}
      </TabsList>
    </Tabs>
  );
};
